// frontend/src/pages/dashboard/TaskComponents/TaskHistoryLog.js
import React, { useEffect, useState, memo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Chip,
  Avatar,
  Box,
  Divider,
  Tooltip,
  CircularProgress,
  Button,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  AutoFixHigh as AutoIcon,
  Edit as ManualIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import moment from 'moment-timezone';

// ─────────────────────────────────────────────────────────────
// Constants & Cache Configuration
// ─────────────────────────────────────────────────────────────
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL
const LIMIT = 20;
const DEBOUNCE_MS = 300; // Debounce rapid fetch attempts

// Global cache with TTL tracking (shared across component instances)
const globalCache = new Map();

// ─────────────────────────────────────────────────────────────
// Skeleton Loader
// ─────────────────────────────────────────────────────────────
const HistoryLogSkeleton = ({ count = 3 }) => (
  <List sx={{ py: 0 }}>
    {[...Array(count)].map((_, i) => (
      <React.Fragment key={i}>
        <ListItem alignItems="flex-start" sx={{ py: 1.5, px: 2 }}>
          <ListItemAvatar sx={{ minWidth: 36, mt: 0.5 }}>
            <Skeleton variant="circular" width={28} height={28} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton width="60%" height={20} />}
            secondary={
              <>
                <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
                <Skeleton width="80%" height={16} sx={{ mt: 0.5 }} />
              </>
            }
          />
        </ListItem>
        {i < count - 1 && <Divider variant="inset" component="li" sx={{ mx: 2 }} />}
      </React.Fragment>
    ))}
  </List>
);

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const TaskHistoryLog = memo(({ taskId, company, theme: parentTheme }) => {
  const defaultTheme = useTheme();
  const theme = parentTheme || defaultTheme;
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  
  // Refs for debouncing and abort control
  const debounceTimer = useRef(null);
  const abortController = useRef(null);
  const lastFetchTime = useRef(0);

  // ─────────────────────────────────────────────────────────
  // Check if cache is valid (not expired)
  // ─────────────────────────────────────────────────────────
  const isCacheValid = useCallback((cacheEntry) => {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < CACHE_TTL;
  }, []);

  // ─────────────────────────────────────────────────────────
  // Fetch with debounce, abort, and smart caching
  // ─────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (taskIdToFetch, pageNum = 1, append = false) => {
    if (!taskIdToFetch) return;

    // Debounce rapid calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    return new Promise((resolve) => {
      debounceTimer.current = setTimeout(async () => {
        // Abort previous request if still pending
        if (abortController.current) {
          abortController.current.abort();
        }
        abortController.current = new AbortController();

        const targetLoading = append ? setFetchingMore : setLoading;
        targetLoading(true);
        setError(null);

        try {
          // Check global cache first
          const cacheKey = `${taskIdToFetch}_page_${pageNum}`;
          const cached = globalCache.get(cacheKey);
          
          if (isCacheValid(cached) && !append) {
            setLogs(cached.logs);
            setPage(cached.page);
            setHasMore(cached.hasMore);
            setLoading(false);
            resolve();
            return;
          }

          const token = localStorage.getItem('access_token');
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/tasks/${taskIdToFetch}/history`,
            { 
              headers: { Authorization: `Bearer ${token}` },
              params: { page: pageNum, limit: LIMIT },
              signal: abortController.current.signal
            }
          );
          
          const responseData = res.data;
          const newLogs = Array.isArray(responseData.logs) 
            ? responseData.logs 
            : Array.isArray(responseData) 
              ? responseData 
              : [];
          
          const total = responseData.total ?? responseData.length ?? 0;
          
          // Update cache
          const cacheEntry = {
            logs: append ? [...(logs || []), ...newLogs] : newLogs,
            page: append ? pageNum : 1,
            hasMore: newLogs.length === LIMIT && (append ? logs.length + newLogs.length < total : newLogs.length < total),
            timestamp: Date.now()
          };
          globalCache.set(cacheKey, cacheEntry);
          
          if (!append) {
            setLogs(newLogs);
            setPage(1);
            setHasMore(newLogs.length === LIMIT && newLogs.length < total);
          } else {
            setLogs(prev => [...prev, ...newLogs]);
            setPage(pageNum);
            setHasMore(newLogs.length === LIMIT && (logs?.length || 0) + newLogs.length < total);
          }
          
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('Fetch aborted');
            return;
          }
          console.error('Failed to fetch history:', err);
          setError('Unable to load activity log');
          if (!append) setLogs([]);
        } finally {
          targetLoading(false);
          resolve();
        }
      }, DEBOUNCE_MS);
    });
  }, [isCacheValid]);

  // ─────────────────────────────────────────────────────────
  // Initial fetch with cache check
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!taskId) return;

    const cacheKey = `${taskId}_page_1`;
    const cached = globalCache.get(cacheKey);
    
    if (isCacheValid(cached)) {
      setLogs(cached.logs);
      setPage(cached.page);
      setHasMore(cached.hasMore);
      setLoading(false);
      return;
    }

    fetchLogs(taskId, 1, false);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
    };
  }, [taskId, fetchLogs, isCacheValid]);

  // ─────────────────────────────────────────────────────────
  // Cleanup on unmount
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
    };
  }, []);

  // ─────────────────────────────────────────────────────────
  // Memoized helpers
  // ─────────────────────────────────────────────────────────
  const formatTime = useCallback((dateString) => {
    if (!dateString) return '—';
    const tz = company?.timezone || 'UTC';
    return moment.utc(dateString).tz(tz).format('MMM DD, YYYY • h:mm A');
  }, [company?.timezone]);

  const getLogIcon = useCallback((reason) => {
    switch (reason) {
      case 'overdue_auto': return <WarningIcon color="error" fontSize="small" />;
      case 'auto':
      case 'system': return <AutoIcon color="primary" fontSize="small" />;
      default: return <ManualIcon color="action" fontSize="small" />;
    }
  }, []);

  const getChipProps = useCallback((reason) => {
    switch (reason) {
      case 'overdue_auto': return { label: 'Overdue', color: 'error', variant: 'filled' };
      case 'auto':
      case 'system': return { label: 'System', color: 'primary', variant: 'outlined' };
      default: return { label: 'Manual', color: 'default', variant: 'outlined' };
    }
  }, []);

  // ─────────────────────────────────────────────────────────
  // Render states
  // ─────────────────────────────────────────────────────────
  if (loading && logs.length === 0) return <HistoryLogSkeleton count={3} />;
  
  if (error && logs.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <WarningIcon color="error" sx={{ mb: 1 }} />
        <Typography variant="body2" color="error" gutterBottom>{error}</Typography>
        <Button size="small" onClick={() => fetchLogs(taskId, 1, false)} sx={{ mt: 1 }}>Retry</Button>
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <TimeIcon color="action" sx={{ mb: 1, opacity: 0.6 }} />
        <Typography variant="body2" color="text.secondary">No activity history yet</Typography>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Main list render
  // ─────────────────────────────────────────────────────────
  return (
    <>
      <List sx={{ py: 0 }}>
        {logs.map((log, index) => {
          const chipProps = getChipProps(log.reason);
          const isOverdue = log.reason === 'overdue_auto';
          const changedBy = log.changed_by;
          
          return (
            <React.Fragment key={log.id || `${log.occurred_at}-${index}`}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  py: 1.5, px: 2,
                  bgcolor: isOverdue ? 'error.50' : 'transparent',
                  borderLeft: isOverdue ? `3px solid ${theme.palette.error.main}` : '3px solid transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <ListItemAvatar sx={{ minWidth: 36, mt: 0.5 }}>
                  <Tooltip title={log.reason === 'overdue_auto' ? 'Auto-triggered' : 'User action'} arrow>
                    <Avatar sx={{
                      width: 28, height: 28, fontSize: '0.875rem',
                      bgcolor: isOverdue ? 'error.light' : log.reason === 'auto' ? 'primary.light' : 'grey.300',
                      color: isOverdue ? 'error.contrastText' : log.reason === 'auto' ? 'primary.contrastText' : 'grey.800',
                    }}>
                      {getLogIcon(log.reason)}
                    </Avatar>
                  </Tooltip>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={600} component="span" sx={{ lineHeight: 1.3 }}>
                        {log.from_status || '—'} → {log.to_status || '—'}
                      </Typography>
                      <Chip {...chipProps} size="small" sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75, py: 0 } }} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <TimeIcon fontSize="inherit" sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{formatTime(log.occurred_at)}</Typography>
                      </Box>
                      {changedBy && (
                        <Typography variant="caption" color="text.primary" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <PersonIcon fontSize="inherit" sx={{ fontSize: '0.75rem' }} />
                          {changedBy.name || changedBy.email || 'System'}
                        </Typography>
                      )}
                      {log.notes && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, pl: 1, borderLeft: `2px solid ${theme.palette.divider}`, color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                          {log.notes}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < logs.length - 1 && <Divider variant="inset" component="li" sx={{ mx: 2 }} />}
            </React.Fragment>
          );
        })}
      </List>
      
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {fetchingMore ? (
            <CircularProgress size={20} />
          ) : (
            <Button size="small" onClick={() => fetchLogs(taskId, page + 1, true)} startIcon={<AutoIcon fontSize="small" />} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Load Older Activity
            </Button>
          )}
        </Box>
      )}
    </>
  );
});

export default TaskHistoryLog;